/* eslint-disable jsx-a11y/heading-has-content */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useState } from "react";
import { NAV_LIST_CART_CONTROLLER } from "../../../../dummyData/FoodItemData";
import { TabContent, TabPane } from "reactstrap";
import "./index.css";
import PillNavigation from "../../../../components/SubNavBar";
import { Card, CardBody, Button, Alert } from "react-bootstrap";
import BreadCrumbs from "../../../../components/BreadCrumbs";
import CartControllerOverView from "./OverView";
import InventoryTable from "./Inventory/InventoryTable";
import { useSelector } from "react-redux";
import {
  useLazyGetCartInventoryForRestaurantQuery,
  useLazyGetPendingCartRequestByRestaurantIdQuery,
} from "../../../../redux/services/restaurantServices";
import { useLocation, useNavigate } from "react-router-dom";
import { CartList } from "..";
import { InventoryId } from "../../../../utility/Utility";
import CartMapView from "../../../../components/CartsLocationMap";
import Sales from "./Sales";
import { ArrowLeft } from "lucide-react";
import { deliverySessions } from "../../../../dummyData/dummyData";
import DeliverySessionList from "./DeliverySession";

export interface InventoryList {
  directIssues: number;
  displayName: string;
  reservedIssues: number;
  totalReceived: number;
  totalRequested: number;
  totalReserved: number;
  totalReturned: number;
  createdDate?: string;
  updatedDate?: string;
  balance?: number;
  variantName?: string;
}

const CartControllerView = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get data from location state
  const locationState =
    (location.state as {
      carts?: CartList;
      activeTab?: number;
      selectedDate?: string;
    }) || {};

  const {
    carts: incomingCarts,
    activeTab,
    selectedDate: incomingDate,
  } = locationState;

  // Store carts in state
  const [carts, setCarts] = useState<CartList | undefined>(incomingCarts);

  // Initialize active tab
  const [active, setActive] = useState(activeTab || 1);

  // Initialize selected date
  const [selectedDate, setSelectedDate] = useState<string>(
    incomingDate || new Date().toISOString().split("T")[0],
  );

  const [inventory, setInventory] = useState<InventoryList[]>([]);

  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails?.id,
  );

  const [
    triggerInventoryForCarts,
    {
      data: inventoryForCarts,
      isLoading: isInventoryForCartsLoading,
      error: inventoryForCartsError,
    },
  ] = useLazyGetCartInventoryForRestaurantQuery();

  const cartId: string = carts?.id || "";

  const inventoryId: string = useMemo(() => {
    const date = new Date(selectedDate);
    return InventoryId(date);
  }, [selectedDate]);

  const [
    triggerCartRequestJoin,
    {
      data: CartRequestJoinData,
      isLoading: isCartRequestJoinDataLoading,
      error: CartRequestJoinDataError,
    },
  ] = useLazyGetPendingCartRequestByRestaurantIdQuery();

  // Update carts when new data comes from navigation
  useEffect(() => {
    if (incomingCarts) {
      setCarts(incomingCarts);
    }
  }, [incomingCarts]);

  // Update active tab when coming from navigation
  useEffect(() => {
    if (activeTab) {
      setActive(activeTab);
    }
  }, [activeTab]);

  // Update selected date when coming from navigation
  useEffect(() => {
    if (incomingDate) {
      setSelectedDate(incomingDate);
    }
  }, [incomingDate]);

  // Handle back to cart list - CLEAR STATE
  const handleBackToCartList = () => {
    // Navigate to cart list with EMPTY state to clear previous cart data
    navigate("/carts/cart-list", {
      state: {}, // Empty state - this is the key fix!
    });
  };

  const toggle = (tab: any) => {
    setActive(tab);
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
  };

  // Fetch inventory data when cartId is available
  useEffect(() => {
    if (restaurantId && cartId && inventoryId) {
      triggerInventoryForCarts({ restaurantId, cartId, inventoryId });
    }
  }, [restaurantId, cartId, inventoryId, triggerInventoryForCarts]);

  useEffect(() => {
    if (inventoryForCarts) {
      const inventoryArray: InventoryList[] = [];

      Object.entries(inventoryForCarts.items).forEach(([itemId, variants]) => {
        Object.entries(variants as Record<string, any>).forEach(
          ([variantName, data]) => {
            inventoryArray.push({
              ...data,
              variantName,
              displayName: `${data.displayName} (${variantName})`,
            });
          },
        );
      });

      setInventory(inventoryArray);
    }
  }, [inventoryForCarts]);

  useEffect(() => {
    if (restaurantId) {
      triggerCartRequestJoin(restaurantId);
    }
  }, [restaurantId, triggerCartRequestJoin]);

  // If no cart data, show error
  if (!carts) {
    return (
      <>
        <BreadCrumbs hasSubMenus={false} />
        <br />
        <Card>
          <CardBody>
            <Alert variant="warning">
              <Alert.Heading>Cart Data Not Found</Alert.Heading>
              <p>Please go back and select a cart from the list.</p>
              <hr />
              <div className="d-flex justify-content-between">
                <Button
                  variant="outline-warning"
                  onClick={handleBackToCartList}
                >
                  Go to Cart List
                </Button>
                <Button variant="outline-primary" onClick={() => navigate(-1)}>
                  Go Back
                </Button>
              </div>
            </Alert>
          </CardBody>
        </Card>
      </>
    );
  }

  return (
    <>
      <div className="mb-3">
        <Button
          variant="outline-secondary"
          onClick={handleBackToCartList}
          className="d-flex align-items-center gap-2"
        >
          <ArrowLeft /> Back to Cart List
        </Button>
      </div>

      <Card>
        <CardBody>
          <PillNavigation
            navList={NAV_LIST_CART_CONTROLLER}
            active={active}
            toggle={toggle}
          />
          <TabContent activeTab={active}>
            {active === 1 && (
              <div className="sub-nav-bar-each-part">
                <TabPane tabId={1}>
                  <CartControllerOverView />
                </TabPane>
              </div>
            )}
            {active === 2 && (
              <div className="sub-nav-bar-each-part">
                <TabPane tabId={2}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="inventory-headers mb-0">
                      {carts.displayName} - Inventory
                    </h4>
                    <div className="date-filter" style={{ width: "200px" }}>
                      <label htmlFor="inventory-date" className="form-label">
                        Select Date:
                      </label>
                      <input
                        type="date"
                        id="inventory-date"
                        className="form-control"
                        value={selectedDate}
                        onChange={(e) => handleDateChange(e.target.value)}
                        max={new Date().toISOString().split("T")[0]}
                      />
                    </div>
                  </div>

                  <InventoryTable
                    selectedDate={selectedDate}
                    cartId={cartId}
                    cartName={carts.displayName}
                  />
                </TabPane>
              </div>
            )}
            {active === 3 && (
              <div className="sub-nav-bar-each-part">
                <TabPane tabId={3}>
                  <Sales />
                </TabPane>
              </div>
            )}
            {active === 4 && (
              <div className="sub-nav-bar-each-part">
                <TabPane tabId={4}>
                  <CartMapView />
                </TabPane>
              </div>
            )}
            {active === 5 && (
              <div className="sub-nav-bar-each-part">
                <TabPane tabId={5}>
                  <DeliverySessionList
                    cartId={cartId}
                    cartName={carts.displayName}
                    selectedDate={selectedDate}
                  />
                </TabPane>
              </div>
            )}
          </TabContent>
        </CardBody>
      </Card>
    </>
  );
};

export default CartControllerView;
